import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Camera, Save, Shield, ShieldCheck, HeartHandshake, Users, User as UserIcon, Lock, Mail, CreditCard, AlertOctagon, ExternalLink, Bell, PawPrint, Compass, MapPin, Stethoscope, TreePine, AlertTriangle, ChevronRight, Globe, Sparkles, RefreshCw, FileText, XCircle, Loader2, X, Eye, EyeOff, Dot } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { useAuth } from "@/context/AuthContext";
import KarmaBadge from "@/components/KarmaBadge";
import AchievementGallery from "@/components/AchievementGallery";
import PushToggle from "@/components/PushToggle";
import ReferralCard from "@/components/ReferralCard";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { useSubscription } from "@/hooks/useSubscription";

const INTERESTS = [
  { id: "passeggiate", label: "Passeggiate quotidiane", color: "#34D399" },
  { id: "sport", label: "Sport cinofili", color: "#FFB020" },
  { id: "addestramento", label: "Addestramento", color: "#FFB020" },
  { id: "attivismo", label: "Attivismo & soccorso", color: "#FFB020" },
  { id: "rifugio", label: "Volontariato rifugio", color: "#FFB020" },
  { id: "cuccioli", label: "Cuccioli & crescita", color: "#34D399" },
  { id: "veterinaria", label: "Cure veterinarie", color: "#FFB020" },
  { id: "viaggi", label: "Viaggi col cane", color: "#34D399" },
];

const ATTITUDES = [
  { id: "principiante", label: "Sono al primo cane", emoji: "🌱" },
  { id: "esperto", label: "Convivo con cani da anni", emoji: "🏡" },
  { id: "educatore", label: "Educatore / professionista", emoji: "🎓" },
  { id: "veterinario", label: "Medico veterinario", emoji: "⚕️" },
];

/* AppVersionCard — displays the currently installed PWA version and lets the
   user force a check for updates. Useful because some users disable auto-
   reloads or dismiss the update toast; this gives them an explicit lever.
   The version is fetched from /api/app/version which is the source of truth. */
function AppVersionCard() {
  const [serverVer, setServerVer] = useState(null);
  const [checking, setChecking] = useState(false);
  const [outdated, setOutdated] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/app/version");
        if (!mounted) return;
        setServerVer(data.version);
        const boot = localStorage.getItem("orio_boot_version");
        if (boot && data.version && boot !== data.version) setOutdated(true);
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, []);

  const check = async () => {
    setChecking(true);
    try {
      const fn = window.orioCheckForUpdate;
      if (typeof fn !== "function") {
        toast.info("Modalità sviluppo: aggiornamento automatico non attivo.");
        return;
      }
      const r = await fn();
      if (!r?.ok) {
        toast.error("Impossibile controllare gli aggiornamenti. Riprova online.");
        return;
      }
      if (r.updated) {
        toast.success("Nuova versione trovata! Aggiorna con il pulsante in basso.");
      } else {
        toast.success("Sei sull'ultima versione.");
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="orio-card rounded-2xl p-5" data-testid="app-version-card">
      <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Versione app</div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-2xl text-[#1A1A1A] tabular-nums" data-testid="app-version-value">
            v{serverVer || "…"}
          </div>
          <div className="text-[12px] text-[#6B6B6B] mt-0.5">
            {outdated
              ? "È disponibile una nuova versione — tocca Aggiorna."
              : "Sei sull'ultima versione disponibile."}
          </div>
        </div>
        <button
          onClick={check}
          disabled={checking}
          data-testid="app-version-check"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#F4EFE6] hover:bg-[#F0EBE3] text-sm border border-[#F0EBE3] disabled:opacity-60"
        >
          {checking
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <RefreshCw className="h-4 w-4" />}
          {checking ? "Controllo…" : "Cerca aggiornamenti"}
        </button>
      </div>
      {outdated && (
        <div className="mt-3 rounded-xl px-3 py-2 text-[12px]"
          style={{ background: "linear-gradient(120deg, #FFF6E6 0%, #FFEDD5 60%, #FFE0F0 100%)", color: "#C25612" }}>
          Per applicare l&apos;aggiornamento, chiudi e riapri l&apos;app oppure tocca Aggiorna nella barra in basso quando compare.
        </div>
      )}
    </div>
  );
}

/* WelcomeTourCard — lets any user re-open the 7-slide welcome onboarding at
   will (great for people who skipped it or want to refresh their memory). */
function WelcomeTourCard() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="orio-card rounded-2xl p-5" data-testid="welcome-tour-card">
        <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Guida di benvenuto</div>
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)",
                     boxShadow: "0 10px 22px -8px rgba(255,122,26,0.45)" }}>
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-[#1A1A1A] leading-tight">
              Rivedi il tour di benvenuto
            </div>
            <div className="text-[12.5px] text-[#6B6B6B] mt-1 leading-snug">
              Sette slide veloci per riscoprire tutte le funzioni ORIO — SOS, Match, Missioni, Cassetto, Karma.
            </div>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="welcome-tour-open"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white"
          style={{
            background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
            boxShadow: "0 12px 26px -10px rgba(255,92,42,0.55)",
          }}
        >
          <PawPrint className="h-4 w-4" /> Riparti dal tour
        </button>
      </div>
      <OnboardingChecklist open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/* ContactCard — lets any user send a feedback/bug/suggestion message to the
   ORIO support inbox. Posts to `/api/support/contact` which forwards to the
   support SMTP address (or logs it if SMTP not configured). Fallback: opens a
   pre-filled mailto: link if the API call fails. */
function ContactCard() {
  const { user } = useAuth();
  const SUPPORT_EMAIL = "supporto@oriodog.com";
  const [open, setOpen] = React.useState(false);
  const [category, setCategory] = React.useState("suggerimento");
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);

  const CATS = [
    { id: "suggerimento", label: "Suggerimento", emoji: "💡" },
    { id: "bug",          label: "Bug / Errore tecnico", emoji: "🐞" },
    { id: "problema",     label: "Problema account", emoji: "🛟" },
    { id: "altro",        label: "Altro", emoji: "✉️" },
  ];

  const send = async () => {
    if (message.trim().length < 5) {
      toast.error("Scrivi almeno qualche parola per aiutarci a capire");
      return;
    }
    setSending(true);
    try {
      await api.post("/support/contact", { category, message });
      toast.success("Messaggio inviato! Rispondiamo entro 24 h.");
      setMessage("");
      setOpen(false);
    } catch (err) {
      // Fallback → mailto with prefilled subject and body
      const subject = encodeURIComponent(`ORIO ${CATS.find((c) => c.id === category)?.label || ""}`);
      const body = encodeURIComponent(
        `Utente: ${user?.email || ""}\nID: ${user?.id || ""}\nApp: v${window.orioBootVersion || ""}\n\n${message}`
      );
      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
      toast("Apro il tuo client mail per l'invio…");
    } finally { setSending(false); }
  };

  return (
    <>
      <div className="orio-card rounded-2xl p-5" data-testid="contact-card">
        <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Contatti · Feedback</div>
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white"
            style={{ background: "linear-gradient(135deg, #5B9EE0 0%, #7B61FF 100%)",
                     boxShadow: "0 10px 22px -8px rgba(91,158,224,0.45)" }}>
            <Mail className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-[#1A1A1A] leading-tight">
              Scrivici
            </div>
            <div className="text-[12.5px] text-[#6B6B6B] mt-1 leading-snug">
              Suggerimenti, bug, problemi tecnici o domande. Rispondiamo entro 24 ore.
            </div>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="contact-open"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white"
          style={{
            background: "linear-gradient(135deg, #5B9EE0 0%, #7B61FF 100%)",
            boxShadow: "0 12px 26px -10px rgba(123,97,255,0.55)",
          }}
        >
          <Mail className="h-4 w-4" /> Invia un messaggio
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1600] flex items-end sm:items-center justify-center"
            data-testid="contact-modal"
            onClick={() => !sending && setOpen(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div
              initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="relative w-full sm:max-w-md mx-3 rounded-t-[28px] sm:rounded-[28px] border border-[#F0EBE3] bg-white p-6 overflow-hidden"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white"
                  style={{ background: "linear-gradient(135deg, #5B9EE0 0%, #7B61FF 100%)" }}>
                  <Mail className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-[#5B4EA1]">Scrivici</div>
                  <div className="font-display text-2xl leading-tight text-[#1A1A1A] mt-0.5">
                    Come possiamo aiutarti?
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Chiudi"
                  data-testid="contact-close"
                  className="h-9 w-9 rounded-full bg-[#F4EFE6] flex items-center justify-center hover:bg-[#EAE3D2]"
                >
                  <X className="h-4 w-4 text-[#6B6B6B]" />
                </button>
              </div>

              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.20em] text-[#7A7A7A] font-semibold mb-2">Categoria</div>
                <div className="grid grid-cols-2 gap-2">
                  {CATS.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setCategory(c.id)}
                      data-testid={`contact-cat-${c.id}`}
                      className="rounded-2xl border p-3 text-left transition-all"
                      style={{
                        borderColor: category === c.id ? "#7B61FF" : "#F0EBE3",
                        background: category === c.id ? "#F1ECFF" : "white",
                      }}
                    >
                      <div className="text-lg leading-none">{c.emoji}</div>
                      <div className="text-[12.5px] font-semibold text-[#1A1A1A] mt-1">{c.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <label className="block mt-4">
                <span className="text-[10px] uppercase tracking-[0.20em] text-[#7A7A7A] font-semibold">Messaggio</span>
                <textarea
                  data-testid="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="Racconta cosa è successo, cosa vorresti, oppure il bug che hai trovato…"
                  className="mt-1.5 w-full px-4 py-3 rounded-2xl border bg-white text-[14px] resize-none"
                  style={{ borderColor: "#F0EBE3" }}
                />
              </label>

              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={send}
                disabled={sending}
                data-testid="contact-send"
                className="mt-5 w-full py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                style={{
                  background: "linear-gradient(135deg, #5B9EE0 0%, #7B61FF 100%)",
                  boxShadow: "0 16px 32px -12px rgba(123,97,255,0.55)",
                }}
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Invia
              </motion.button>
              <p className="mt-3 text-[11px] text-[#9A9A9A] text-center">
                Risposta entro 24 ore all'email {user?.email || "del tuo account"}.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


export default function UserProfile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Legacy deep-link support: /app/profilo?tab=abbonamento now redirects
  // straight to the dark /billing page. Kept so old TrialBanner / push links
  // don't 404.
  useEffect(() => {
    if (params.get("tab") === "abbonamento") {
      navigate("/billing", { replace: true });
    }
  }, [params, navigate]);
  const [tab, setTab] = useState(params.get("tab") || "pubblico");
  const [subtab, setSubtab] = useState(params.get("sub") || "identita");
  const [data, setData] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dogCount, setDogCount] = useState(0);

  useEffect(() => {
    api.get("/dogs").then(({ data }) => {
      setDogCount(data.filter((d) => !d.is_stub).length);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setData({
        name: user.name || "",
        bio: user.bio || "",
        city: user.city || "",
        phone: user.phone || "",
        birth_year: user.birth_year || "",
        interests: user.interests || [],
        dog_attitude: user.dog_attitude || "",
        is_shelter: user.is_shelter || false,
        shelter_name: user.shelter_name || "",
        radius_prefs: user.radius_prefs || { missions: 25, vet: 25, parks: 15, hazards: 8 },
      });
      setPhotoPreview(user.photo_url || null);
    }
  }, [user]);

  const toggleInterest = (id) => {
    const set = new Set(data.interests || []);
    set.has(id) ? set.delete(id) : set.add(id);
    setData((d) => ({ ...d, interests: Array.from(set) }));
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Hard ceiling on raw uploads (25 MB) — anything bigger is almost certainly
    // wrong (RAW, huge screenshot, video). Below that we compress client-side.
    if (f.size > 25 * 1024 * 1024) return toast.error("File troppo grande (max 25 MB)");
    try {
      const dataUrl = await compressImage(f, { maxSide: 1280, quality: 0.85 });
      setPhotoPreview(dataUrl);
      setData((d) => ({ ...d, photo_url: dataUrl }));
    } catch (err) {
      console.warn("compress error", err);
      toast.error("Impossibile leggere la foto. Riprova con un'altra.");
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const payload = { ...data };
      // birth_year: coerce empty string to null so Pydantic doesn't 422 on
      // "unable to parse string as an integer". Any garbage / non-numeric
      // input becomes null too.
      const by = payload.birth_year;
      if (by === "" || by === null || by === undefined) {
        payload.birth_year = null;
      } else {
        const n = parseInt(by, 10);
        payload.birth_year = Number.isFinite(n) ? n : null;
      }
      const { data: updated } = await api.put("/users/me", payload);
      await refresh();
      if (updated?._just_awarded_profile_karma) {
        toast.success("Profilo al 100% — +20 karma! 🎉", { duration: 5000 });
      } else {
        toast.success("Profilo aggiornato");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // Profile completion %
  const completion = computeCompletion(data, photoPreview);

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-[calc(env(safe-area-inset-bottom,0px)+120px)] md:pb-10 relative min-w-0">
      {/* Soft brand glow */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-64 pointer-events-none -z-0"
        style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(255,176,32,0.10) 0%, rgba(0,0,0,0) 70%)" }}
      />

      <div className="relative z-10 min-w-0">
        {/* Hero: centered avatar with completion ring + name + dog count */}
        <div className="flex flex-col items-center text-center" data-testid="profile-hero">
          <ProgressAvatar
            photo={photoPreview}
            name={data.name || user?.name}
            percent={completion}
            onUpload={onPhoto}
          />
          <div className="mt-4 font-display text-2xl sm:text-3xl tracking-tight">
            {data.name || user?.name || "Cittadino ORIO"}
          </div>
          <div className="mt-1 text-[12px] text-[#9A9A9A] inline-flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <PawPrint className="h-3 w-3 text-[#34D399]" />
              {dogCount === 0 ? "Nessun cane" : dogCount === 1 ? "1 cane" : `${dogCount} cani`}
            </span>
            <span className="text-[#1A1A1A]/25">·</span>
            <span className="inline-flex items-center gap-1" style={{ color: completion < 60 ? "#FFB020" : "#34D399" }}>
              {completion}% profilo completato
            </span>
          </div>
          {completion < 100 && (
            <div className="mt-3 text-[11px] text-[#9A9A9A] max-w-xs">
              Completa il profilo per essere più riconoscibile a match, missioni e rifugi.
              {completion < 80 && " (+20 karma quando arrivi al 100%)"}
            </div>
          )}
        </div>

        {/* Shelter CTA / Dashboard link — adaptive per stato rifugio */}
        <ShelterCTASection />

        {/* Tabs — iOS-style pill segments, scrollable */}
        <div className="mt-8 -mx-4 sm:mx-0 px-4 sm:px-0 flex gap-1.5 overflow-x-auto no-scrollbar pb-1 max-w-[100vw]" role="tablist">
          {[
            { id: "pubblico", label: "Pubblico", icon: UserIcon },
            { id: "account", label: "Account", icon: Mail },
            { id: "abbonamento", label: "Abbonamento", icon: CreditCard, external: "/billing" },
            { id: "notifiche", label: "Notifiche", icon: Bell },
            { id: "sicurezza", label: "Sicurezza", icon: Lock },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                data-testid={`prof-tab-${t.id}`}
                onClick={() => t.external ? navigate(t.external) : setTab(t.id)}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-medium transition-all"
                style={{
                  background: active ? "#FF7A1A" : "#FFFFFF",
                  border: `1px solid ${active ? "#FF7A1A" : "#F0EBE3"}`,
                  color: active ? "#FFFFFF" : "#6B6B6B",
                  boxShadow: active ? "0 4px 12px -4px rgba(255,122,26,0.4)" : "0 2px 8px -4px rgba(0,0,0,0.04)",
                }}
              >
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "pubblico" && (
        <form onSubmit={save} className="mt-8 space-y-6">
          {/* Quick links: my dogs + visit website */}
          <div className="grid gap-3 grid-cols-2">
            <Link
              to="/app/cani"
              data-testid="profile-link-cani"
              className="rounded-2xl bg-white border border-[#F0EBE3] p-4 flex items-center gap-3 hover:border-[#FFB020]/55 hover:bg-[#FFFBF2] transition-all active:scale-[0.98]"
              style={{ boxShadow: "0 4px 14px -6px rgba(0,0,0,0.06)" }}
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "rgba(255,176,32,0.15)", color: "#FF7A1A" }}>
                <PawPrint className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#1A1A1A]">I miei cani</div>
                <div className="text-[11px] text-[#7A7A7A] truncate">Schede e passaporti</div>
              </div>
              <ChevronRight className="h-4 w-4 text-[#9A9A9A]" />
            </Link>
            <a
              href="https://oriodog.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="profile-link-landing"
              className="rounded-2xl bg-white border border-[#F0EBE3] p-4 flex items-center gap-3 hover:border-[#5B9EE0]/55 hover:bg-[#F2F7FD] transition-all active:scale-[0.98]"
              style={{ boxShadow: "0 4px 14px -6px rgba(0,0,0,0.06)" }}
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "rgba(91,158,224,0.15)", color: "#5B9EE0" }}>
                <Globe className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#1A1A1A]">Visita sito web</div>
                <div className="text-[11px] text-[#7A7A7A] truncate">oriodog.com</div>
              </div>
              <ChevronRight className="h-4 w-4 text-[#9A9A9A]" />
            </a>
          </div>

          {/* Karma + Push toggle + Referral */}
          <div className="grid gap-4 sm:grid-cols-2">
            {user?.karma && <KarmaBadge karma={user.karma} />}
            <PushToggle />
          </div>

          {/* Achievement gallery — visual collection of badges */}
          <AchievementGallery />

          <ReferralCard />

          {/* Sub-section pills — horizontal swipe on mobile (thumb friendly) */}
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 flex gap-1.5 overflow-x-auto no-scrollbar pb-1 sticky top-14 sm:static z-20 bg-[#FBF7F1] sm:bg-transparent backdrop-blur-md sm:backdrop-blur-none py-2 sm:py-0 max-w-[100vw]">
            {[
              { id: "identita",   label: "Identità",   icon: UserIcon },
              { id: "bio",        label: "Bio",        icon: Mail },
              { id: "esperienza", label: "Esperienza", icon: HeartHandshake },
              { id: "interessi",  label: "Interessi",  icon: Users },
              { id: "distanze",   label: "Distanze",   icon: Compass },
              { id: "ruolo",      label: "Ruolo",      icon: Shield },
            ].map((s) => {
              const active = subtab === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`subtab-${s.id}`}
                  onClick={() => setSubtab(s.id)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-all border"
                  style={{
                    background: active ? "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)" : "#FFFFFF",
                    borderColor: active ? "transparent" : "#F0EBE3",
                    color: active ? "#FFFFFF" : "#6B6B6B",
                    boxShadow: active ? "0 6px 18px -4px rgba(255,122,26,0.45)" : "none",
                  }}
                >
                  <s.icon className="h-3.5 w-3.5" /> {s.label}
                </button>
              );
            })}
          </div>

          {/* Identità */}
          {subtab === "identita" && (
          <div className="orio-card rounded-2xl p-5">
            <label className="text-xs uppercase tracking-widest text-[#9A9A9A] mb-3 block">Dati identità</label>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nome visibile" testid="prof-name" value={data.name} onChange={(v) => setData((d) => ({ ...d, name: v }))} />
              <Field label="Città" testid="prof-city" value={data.city} onChange={(v) => setData((d) => ({ ...d, city: v }))} />
              <Field label="Anno di nascita" testid="prof-year" type="number" value={data.birth_year} onChange={(v) => setData((d) => ({ ...d, birth_year: v }))} />
              <Field label="Telefono (privato)" testid="prof-phone" value={data.phone} onChange={(v) => setData((d) => ({ ...d, phone: v }))} />
            </div>
            <p className="text-[11px] text-[#9A9A9A] mt-3">
              La tua foto si imposta dall&apos;header sopra toccando l&apos;icona camera. Solo nome, foto e bio sono pubblici.
            </p>
          </div>
          )}

          {/* Bio */}
          {subtab === "bio" && (
          <div className="orio-card rounded-2xl p-5">
            <label className="text-xs uppercase tracking-widest text-[#9A9A9A]">Bio (visibile)</label>
            <textarea
              data-testid="prof-bio"
              value={data.bio || ""}
              onChange={(e) => setData((d) => ({ ...d, bio: e.target.value }))}
              placeholder="Racconta in poche righe chi sei, che rapporto hai con i cani, cosa cerchi su ORIO."
              maxLength={280}
              className="mt-2 w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors min-h-[140px]"
            />
            <div className="mt-1 text-right text-xs text-[#9A9A9A]">{(data.bio || "").length}/280</div>
          </div>
          )}

          {/* Esperienza */}
          {subtab === "esperienza" && (
          <div className="orio-card rounded-2xl p-5">
            <label className="text-xs uppercase tracking-widest text-[#9A9A9A] mb-3 block">La tua esperienza</label>
            <div className="grid sm:grid-cols-2 gap-2">
              {ATTITUDES.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`att-${a.id}`}
                  onClick={() => setData((d) => ({ ...d, dog_attitude: a.id }))}
                  className={`text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                    data.dog_attitude === a.id
                      ? "bg-[#FFF6E5] border-[#FFB020] text-[#1A1A1A] shadow-[0_4px_14px_-4px_rgba(255,176,32,0.35)]"
                      : "bg-white border-[#F0EBE3] text-[#5C5C5C] hover:bg-[#FAF7F0]"
                  }`}
                >
                  <span className="mr-2">{a.emoji}</span>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Interessi */}
          {subtab === "interessi" && (
          <div className="orio-card rounded-2xl p-5">
            <label className="text-xs uppercase tracking-widest text-[#9A9A9A] mb-3 block">Interessi</label>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => {
                const active = (data.interests || []).includes(i.id);
                return (
                  <button
                    key={i.id}
                    type="button"
                    data-testid={`int-${i.id}`}
                    onClick={() => toggleInterest(i.id)}
                    className="px-3.5 py-2 rounded-full text-xs font-medium border transition-all"
                    style={{
                      background: active ? `${i.color}1A` : "#FFFFFF",
                      borderColor: active ? `${i.color}80` : "#F0EBE3",
                      color: active ? i.color : "#5C5C5C",
                      boxShadow: active ? `0 4px 14px -4px ${i.color}50` : "none",
                    }}
                  >
                    {i.label}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Distanze d'azione */}
          {subtab === "distanze" && (
          <div className="space-y-4">
            <div className="orio-card rounded-2xl p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "rgba(255,176,32,0.15)", color: "#FFB020", border: "1px solid rgba(255,176,32,0.30)" }}>
                  <Compass className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB020]">Raggio d&apos;azione</div>
                  <div className="font-display text-lg mt-0.5">Quanto vuoi vedere e agire?</div>
                  <p className="text-[12px] text-[#9A9A9A] mt-1 leading-relaxed">
                    Imposta i km entro cui ricevere notifiche e vedere POI/missioni sulla mappa.
                    Più piccolo il raggio, meno notifiche; più grande, più copertura.
                  </p>
                </div>
              </div>
              <div className="space-y-5">
                <RadiusSlider
                  icon={HeartHandshake} color="#FFB020" label="Missioni"
                  testid="rad-missions"
                  value={data.radius_prefs?.missions ?? 25}
                  onChange={(v) => setData((d) => ({ ...d, radius_prefs: { ...d.radius_prefs, missions: v } }))}
                />
                <RadiusSlider
                  icon={Stethoscope} color="#34D399" label="Veterinari"
                  testid="rad-vet"
                  value={data.radius_prefs?.vet ?? 25}
                  onChange={(v) => setData((d) => ({ ...d, radius_prefs: { ...d.radius_prefs, vet: v } }))}
                />
                <RadiusSlider
                  icon={TreePine} color="#34D399" label="Aree cani / parchi"
                  testid="rad-parks"
                  value={data.radius_prefs?.parks ?? 15}
                  onChange={(v) => setData((d) => ({ ...d, radius_prefs: { ...d.radius_prefs, parks: v } }))}
                />
                <RadiusSlider
                  icon={AlertTriangle} color="#FFB020" label="Pericoli (min 8 km · sicurezza)"
                  testid="rad-hazards"
                  min={8}
                  value={Math.max(8, data.radius_prefs?.hazards ?? 8)}
                  onChange={(v) => setData((d) => ({ ...d, radius_prefs: { ...d.radius_prefs, hazards: Math.max(8, v) } }))}
                />
              </div>
              <div className="mt-4 rounded-xl px-3 py-2.5 flex items-start gap-2 text-[11.5px] leading-relaxed"
                style={{ background: "rgba(255,176,32,0.10)", border: "1px solid rgba(255,176,32,0.24)", color: "#7A6A44" }}>
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#FFB020]" />
                <span>
                  <strong className="text-[#5C4A1F]">Pericoli sempre attivi.</strong> Il raggio minimo è 8 km per tutti — è la garanzia ORIO: se qualcuno segnala un veleno, un cane pericoloso, un cantiere, tu ricevi la notifica se sei nei dintorni. Anche in modalità invisibile.
                </span>
              </div>
            </div>

            {/* Presenza Live — 15km fixed presence explainer */}
            <PresenzaLiveCard />

            {/* SOS dynamic radius — fixed, informational */}
            <div className="rounded-2xl p-5 border"
              style={{ background: "linear-gradient(160deg, rgba(255,59,48,0.10) 0%, rgba(14,14,16,0.65) 70%)", borderColor: "rgba(255,59,48,0.25)" }}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "rgba(255,59,48,0.18)", color: "#FF3B30", border: "1px solid rgba(255,59,48,0.35)" }}>
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#FF3B30]">SOS Emergenza · dinamico</div>
                  <div className="font-display text-lg mt-0.5">L&apos;allarme cresce nel tempo</div>
                  <p className="text-[12px] text-[#6B6B6B] mt-2 leading-relaxed">
                    Dichiarando un&apos;emergenza si parte con un allarme entro <strong className="text-[#1A1A1A]">10 km</strong> nei primi 20 minuti,
                    poi esteso a <strong className="text-[#1A1A1A]">25 km</strong>, <strong className="text-[#1A1A1A]">40 km</strong>,
                    <strong className="text-[#1A1A1A]"> 60 km</strong> e fino a <strong className="text-[#1A1A1A]">80 km</strong> dal punto di partenza —
                    in base a quanto un cane può allontanarsi nel tempo.
                  </p>
                  <div className="mt-3 grid grid-cols-5 gap-1.5">
                    {[
                      { t: "0-20m", r: "10 km" },
                      { t: "20-40m", r: "25 km" },
                      { t: "40-60m", r: "40 km" },
                      { t: "1-2h", r: "60 km" },
                      { t: "2h+", r: "80 km" },
                    ].map((s) => (
                      <div key={s.t} className="rounded-lg px-1.5 py-2 text-center border"
                        style={{ background: "rgba(255,59,48,0.06)", borderColor: "rgba(255,59,48,0.20)" }}>
                        <div className="text-[9px] uppercase tracking-wider text-[#9A9A9A]">{s.t}</div>
                        <div className="text-[11px] font-semibold text-[#FF3B30] mt-0.5">{s.r}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Ruolo */}
          {subtab === "ruolo" && (
          <div className="orio-card rounded-2xl p-5">
            <label className="text-xs uppercase tracking-widest text-[#9A9A9A] mb-3 block">Ruolo nella community</label>
            <div className="space-y-3">
              <Toggle
                testid="role-shelter"
                icon={Shield}
                color="#FFB020"
                label="Gestisco un rifugio o associazione"
                sub="Sblocca la dashboard rifugio: missioni da valutare, volontari, candidature di adozione"
                checked={data.is_shelter}
                onChange={(v) => setData((d) => ({ ...d, is_shelter: v }))}
              />
              {data.is_shelter && (
                <Field
                  label="Nome rifugio / associazione"
                  testid="prof-shelter"
                  value={data.shelter_name}
                  onChange={(v) => setData((d) => ({ ...d, shelter_name: v }))}
                />
              )}
            </div>
          </div>
          )}

          {/* Save button — always visible */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              data-testid="prof-submit"
              disabled={loading}
              className="orio-cta-btn relative px-6 py-3.5 rounded-full font-semibold text-black overflow-hidden disabled:opacity-50"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                <Save className="h-4 w-4" /> {loading ? "Salvataggio..." : "Salva profilo"}
              </span>
            </button>
            <span className="text-[11px] text-[#9A9A9A] hidden sm:inline">Le modifiche di tutte le sezioni vengono salvate insieme</span>
          </div>
        </form>
        )}

        {tab === "account" && <AccountTab user={user} onUpdated={refresh} />}
        {tab === "abbonamento" && <SubscriptionTab user={user} />}
        {tab === "notifiche" && <NotificationsTab />}
        {tab === "sicurezza" && <SecurityTab onLoggedOut={logout} />}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#9A9A9A]">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors"
      />
    </div>
  );
}

function Toggle({ label, sub, checked, onChange, icon: Icon, color, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`w-full text-left px-4 py-3 rounded-xl border flex items-center gap-4 transition-all ${
        checked ? "bg-[#FAF7F0]" : "bg-[#FAF7F0] hover:bg-[#FAF7F0]"
      }`}
      style={{ borderColor: checked ? `${color}50` : "rgba(255,255,255,0.1)" }}
    >
      <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: `${color}20`, color }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-[#9A9A9A] mt-0.5">{sub}</div>
      </div>
      <div className={`h-5 w-9 rounded-full transition-all ${checked ? "" : "bg-[#F0EBE3]"}`}
        style={{ background: checked ? color : "rgba(255,255,255,0.1)" }}
      >
        <div className={`h-4 w-4 rounded-full bg-white mt-0.5 transition-all ${checked ? "ml-4" : "ml-0.5"}`} />
      </div>
    </button>
  );
}


/* ============================================================== */
/*                         ACCOUNT TAB                            */
/* ============================================================== */
function AccountTab({ user, onUpdated }) {
  const [newEmail, setNewEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  const [sendingVerify, setSendingVerify] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !pwd) return;
    setEmailLoading(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail, password: pwd });
      toast.success("Email aggiornata. Controlla la posta per verificarla.");
      setNewEmail(""); setPwd("");
      onUpdated?.();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setEmailLoading(false); }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) return toast.error("Almeno 8 caratteri");
    if (newPwd !== confirmPwd) return toast.error("Le password non coincidono");
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd });
      toast.success("Password aggiornata");
      setCurPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setPwdLoading(false); }
  };

  const resendVerify = async () => {
    setSendingVerify(true);
    try {
      const { data } = await api.post("/auth/send-verify");
      if (data?.already) toast.success("Email già verificata");
      else if (data?.sent) toast.success("Email di verifica inviata");
      else toast.success("Link di verifica generato");
    } catch { toast.error("Errore"); }
    finally { setSendingVerify(false); }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="account-tab">
      {/* Current state */}
      <div className="orio-card rounded-2xl p-5">
        <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Account corrente</div>
        <div className="flex items-center gap-3">
          <Mail className="h-4 w-4 text-[#9A9A9A]" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.email}</div>
            <div className="text-[11px] mt-0.5">
              {user?.email_verified
                ? <span style={{color:"#34D399"}}>✓ Verificata</span>
                : <span style={{color:"#FFB020"}}>Non verificata</span>}
            </div>
          </div>
          {!user?.email_verified && (
            <button onClick={resendVerify} disabled={sendingVerify} data-testid="acc-resend-verify"
              className="px-3 py-1.5 rounded-full text-[11px] border bg-[#F4EFE6] hover:bg-[#F0EBE3]">
              {sendingVerify ? "Invio…" : "Reinvia link"}
            </button>
          )}
        </div>
      </div>

      {/* Change email */}
      <form onSubmit={submitEmail} className="orio-card rounded-2xl p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A]">Cambia email</div>
        <input data-testid="acc-new-email" type="email" required value={newEmail} onChange={(e)=>setNewEmail(e.target.value)}
          placeholder="Nuova email" className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors" />
        <input data-testid="acc-email-pwd" type="password" required value={pwd} onChange={(e)=>setPwd(e.target.value)}
          placeholder="Password attuale (conferma)" className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors" />
        <button type="submit" disabled={emailLoading} data-testid="acc-email-submit"
          className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold disabled:opacity-50">
          {emailLoading ? "Aggiorno…" : "Aggiorna email"}
        </button>
        <p className="text-[11px] text-[#9A9A9A]">La nuova email dovrà essere verificata.</p>
      </form>

      {/* Change password */}
      <form onSubmit={submitPwd} className="orio-card rounded-2xl p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A]">Cambia password</div>
        <input data-testid="acc-cur-pwd" type="password" required value={curPwd} onChange={(e)=>setCurPwd(e.target.value)}
          placeholder="Password attuale" className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors" />
        <input data-testid="acc-new-pwd" type="password" required minLength={8} value={newPwd} onChange={(e)=>setNewPwd(e.target.value)}
          placeholder="Nuova password (min 8)" className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors" />
        <input data-testid="acc-confirm-pwd" type="password" required minLength={8} value={confirmPwd} onChange={(e)=>setConfirmPwd(e.target.value)}
          placeholder="Conferma nuova password" className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors" />
        <button type="submit" disabled={pwdLoading} data-testid="acc-pwd-submit"
          className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold disabled:opacity-50">
          {pwdLoading ? "Aggiorno…" : "Aggiorna password"}
        </button>
      </form>
    </div>
  );
}

/* ============================================================== */
/*                       SUBSCRIPTION TAB                         */
/* ============================================================== */
function SubscriptionTab({ user }) {
  const { startTrial, canStartTrial } = useSubscription();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/summary");
      setSummary(data);
    } catch { /* keep null */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
      else toast.error("Impossibile aprire il portale ora");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Errore apertura portale");
    } finally { setBusy(false); }
  };

  // ONE-tap trial activation — no Stripe, no card required.
  const activateTrialNow = async () => {
    setBusy(true);
    try {
      const r = await startTrial();
      if (r) await load();
    } finally { setBusy(false); }
  };

  // Explicit paid checkout (Stripe) — used for:
  //  • Trial users who want to add a payment method proactively
  //  • Users whose trial expired ("Abbonati")
  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Errore avvio checkout");
      setBusy(false);
    }
  };

  const isPro = !!(summary?.is_pro ?? user?.is_pro);
  const status = summary?.subscription_status || user?.subscription_status || "free";
  const inTrial = !!(summary?.in_trial ?? (status === "trialing"));
  const hasCustomer = !!(summary?.stripe_subscription_id || summary?.stripe_customer_id || user?.stripe_customer_id);
  const endDateRaw = inTrial
    ? (summary?.trial_ends_at || summary?.subscription_ends_at || user?.trial_ends_at)
    : (summary?.subscription_ends_at || summary?.trial_ends_at || user?.subscription_ends_at);
  const endDate = endDateRaw ? new Date(endDateRaw) : null;

  const statusMeta = {
    free:      { label: "Piano gratuito",         color: "#9aa0a6" },
    trialing:  { label: "Prova gratuita",         color: "#FFB020" },
    active:    { label: "ORIO Pro · attivo",      color: "#34D399" },
    past_due:  { label: "Pagamento in sospeso",   color: "#FF3B30" },
    canceled:  { label: "Annullato",              color: "#FF3B30" },
    expired:   { label: "Scaduto",                color: "#9aa0a6" },
  }[status] || { label: "Piano gratuito", color: "#9aa0a6" };

  if (loading) {
    return (
      <div className="mt-8 text-[13px] text-[#9A9A9A] inline-flex items-center gap-2" data-testid="subscription-tab">
        <span className="h-3 w-3 rounded-full border-2 border-[#F0EBE3] border-t-[#FF7A1A] animate-spin" />
        Caricamento…
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6" data-testid="subscription-tab">
      {/* Status card */}
      <div
        className="rounded-3xl p-5 sm:p-6 border relative overflow-hidden"
        style={{
          background: isPro ? "rgba(52,211,153,0.08)" : "rgba(255,176,32,0.06)",
          borderColor: isPro ? "rgba(52,211,153,0.35)" : "rgba(255,176,32,0.30)",
        }}
      >
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: `${statusMeta.color}25`, color: statusMeta.color }}
        >
          <CreditCard className="h-3 w-3" /> {statusMeta.label}
        </div>
        <div className="mt-3 font-display text-2xl">
          {isPro ? "4,99 € / mese" : "Piano gratuito attivo"}
        </div>
        {endDate && (
          <div className="text-sm text-[#6B6B6B] mt-1">
            {(() => {
              const daysLeft = Math.max(0, Math.ceil((endDate - new Date()) / 86400000));
              const dateStr = endDate.toLocaleDateString("it-IT");
              const daysStr = daysLeft === 1 ? "1 giorno rimanente" : `${daysLeft} giorni rimanenti`;
              const isGift = summary?.pro_source === "admin_gift" || user?.pro_source === "admin_gift";
              if (status === "trialing")
                return `Prova gratuita fino al ${dateStr} · ${daysStr}`;
              if (status === "active")
                return isGift
                  ? `Regalo ORIO Pro attivo fino al ${dateStr} · ${daysStr}`
                  : `Prossimo rinnovo: ${dateStr} · ${daysStr}`;
              if (status === "canceled")
                return `Resta attivo fino al ${dateStr} · ${daysStr}`;
              return "";
            })()}
          </div>
        )}

        {/* CTA for non-Pro users — activate the in-DB trial (no card) when eligible,
            or fall back to Stripe checkout when the trial was already consumed. */}
        {!isPro && (
          <div className="mt-5 space-y-3">
            <button
              onClick={canStartTrial ? activateTrialNow : startCheckout}
              disabled={busy}
              data-testid="sub-upgrade"
              className="orio-cta-btn relative inline-flex items-center gap-2 px-5 py-3 rounded-full text-black text-sm font-semibold overflow-hidden disabled:opacity-50"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {canStartTrial ? "Inizia 30 giorni gratis" : "Abbonati a ORIO Pro"}
              </span>
            </button>
            <div className="text-[11px] text-[#7A7A7A] leading-relaxed max-w-md">
              {canStartTrial
                ? "✨ Nessuna carta richiesta. Attivi in un tap e per 30 giorni hai tutto Pro. Alla scadenza scegli: abbonarti o restare Free."
                : "🔒 Checkout sicuro Stripe · nessun costo nascosto · disdici in un tap."}
            </div>
          </div>
        )}

        {/* IN-TRIAL user WITHOUT a Stripe customer yet — offer to add a payment
            method so the subscription continues seamlessly after trial. Not
            required; can be dismissed. */}
        {isPro && inTrial && !hasCustomer && (
          <div className="mt-5 rounded-2xl border p-4"
               style={{ background: "linear-gradient(120deg, #FFF6E6 0%, #FFEDD5 60%, #FFE0F0 100%)", borderColor: "rgba(255,122,26,0.30)" }}>
            <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-[#C25612] inline-flex items-center gap-1.5">
              <CreditCard className="h-3 w-3" /> Continua senza interruzioni
            </div>
            <div className="mt-1.5 font-display text-[18px] text-[#1A1A1A] leading-tight">
              Aggiungi un metodo di pagamento
            </div>
            <p className="text-[12.5px] text-[#6B6B6B] mt-1 leading-snug">
              Aggiungilo quando vuoi: alla fine dei 30 giorni ORIO Pro continua senza interruzione.
              Nessun addebito adesso, ti avvisiamo 3 giorni prima del primo rinnovo.
            </p>
            <button
              onClick={startCheckout}
              disabled={busy}
              data-testid="sub-add-payment"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-[13px] font-semibold disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                       boxShadow: "0 12px 26px -10px rgba(255,92,42,0.55)" }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              Aggiungi carta
            </button>
          </div>
        )}

        {/* 4 clear action cards for subscribed users — all open Stripe Portal (IT) */}
        {hasCustomer && (
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              data-testid="sub-action-card"
              onClick={openPortal}
              disabled={busy}
              className="text-left p-4 rounded-2xl border border-[#F0EBE3] bg-white hover:bg-[#FAF7F0] transition-colors disabled:opacity-50"
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-[#FFF4E5] text-[#FF7A1A] mb-2">
                <CreditCard className="h-4 w-4" />
              </div>
              <div className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">Cambia carta</div>
              <div className="text-[11px] text-[#7A7A7A] mt-0.5 leading-tight">Aggiorna il metodo di pagamento</div>
            </button>

            <button
              data-testid="sub-action-plan"
              onClick={openPortal}
              disabled={busy}
              className="text-left p-4 rounded-2xl border border-[#F0EBE3] bg-white hover:bg-[#FAF7F0] transition-colors disabled:opacity-50"
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-[#ECFDF5] text-[#2DBA60] mb-2">
                <RefreshCw className="h-4 w-4" />
              </div>
              <div className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">Cambia piano</div>
              <div className="text-[11px] text-[#7A7A7A] mt-0.5 leading-tight">Vedi altri piani disponibili</div>
            </button>

            <button
              data-testid="sub-action-invoices"
              onClick={openPortal}
              disabled={busy}
              className="text-left p-4 rounded-2xl border border-[#F0EBE3] bg-white hover:bg-[#FAF7F0] transition-colors disabled:opacity-50"
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-[#F0F0FF] text-[#7B61FF] mb-2">
                <FileText className="h-4 w-4" />
              </div>
              <div className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">Storico fatture</div>
              <div className="text-[11px] text-[#7A7A7A] mt-0.5 leading-tight">Scarica ricevute e dettagli</div>
            </button>

            <button
              data-testid="sub-action-cancel"
              onClick={openPortal}
              disabled={busy}
              className="text-left p-4 rounded-2xl border border-[#F0EBE3] bg-white hover:bg-[#FAF7F0] transition-colors disabled:opacity-50"
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-[#FEE2E2] text-[#E94545] mb-2">
                <XCircle className="h-4 w-4" />
              </div>
              <div className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">Disdici abbonamento</div>
              <div className="text-[11px] text-[#7A7A7A] mt-0.5 leading-tight">Nessuna penale · attivo fino al rinnovo</div>
            </button>
          </div>
        )}
        {hasCustomer && (
          <p className="mt-3 text-[11px] text-[#9A9A9A]">
            🔒 Ogni azione ti porta al portale sicuro Stripe (in italiano). Puoi cambiare metodo di pagamento, scaricare le fatture, cambiare o disdire in qualsiasi momento.
          </p>
        )}
      </div>

      {/* Invoices list */}
      {summary?.invoices?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-3 inline-flex items-center gap-1.5">
            <FileText className="h-3 w-3" /> Ultime fatture
          </div>
          <div className="space-y-1.5">
            {summary.invoices.slice(0, 5).map((inv) => (
              <a
                key={inv.id}
                href={inv.hosted_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`sub-invoice-${inv.id}`}
                className="flex items-center gap-3 rounded-xl bg-white border border-[#F0EBE3] px-3 py-2.5 hover:bg-[#FAF7F0] transition-colors"
              >
                <FileText className="h-4 w-4 text-[#9A9A9A]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1A1A1A]">
                    {new Date(inv.created).toLocaleDateString("it-IT")} · {inv.amount_paid.toFixed(2)} {inv.currency.toUpperCase()}
                  </div>
                  <div className="text-[10px] text-[#9A9A9A]">{inv.status}</div>
                </div>
                <ExternalLink className="h-3 w-3 text-[#9A9A9A]" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
/*                       NOTIFICATIONS TAB                        */
/* ============================================================== */
function NotificationsTab() {
  return (
    <div className="mt-8 space-y-5" data-testid="notifications-tab">
      <PushToggle />
      <ReferralCard />
    </div>
  );
}

/* ============================================================== */
/*                         SECURITY TAB                           */
/* ============================================================== */
function SecurityTab({ onLoggedOut }) {
  const [confirm, setConfirm] = useState(false);
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const del = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    if (!window.confirm("Sei davvero sicuro? L'azione è irreversibile.")) return;
    setLoading(true);
    try {
      await api.post("/auth/delete-account", { password: pwd });
      toast.success("Account cancellato. A presto.");
      await onLoggedOut?.();
      window.location.href = "/";
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-8 space-y-5" data-testid="security-tab">
      <AppVersionCard />
      <WelcomeTourCard />
      <ContactCard />

      <div className="orio-card rounded-2xl p-5">
        <div className="text-[11px] uppercase tracking-widest text-[#9A9A9A] mb-2">Sessione</div>
        <p className="text-sm text-[#6B6B6B] mb-3">
          Esci dal tuo account ORIO su questo dispositivo.
        </p>
        <button onClick={onLoggedOut} data-testid="sec-logout"
          className="px-5 py-2.5 rounded-full bg-[#F4EFE6] hover:bg-[#F0EBE3] text-sm border border-[#F0EBE3]">
          Logout
        </button>
      </div>

      <div className="rounded-2xl p-5" style={{background:"rgba(255,59,48,0.05)", border:"1px solid rgba(255,59,48,0.30)"}}>
        <div className="flex items-center gap-2 mb-2 text-[#FF3B30]">
          <AlertOctagon className="h-4 w-4" />
          <div className="text-[11px] uppercase tracking-widest font-medium">Zona pericolosa</div>
        </div>
        <p className="text-sm text-[#1A1A1A] mb-3">
          Cancellando l&apos;account perdi tutti i passaporti, le missioni e il karma. L&apos;azione è irreversibile.
        </p>
        {!confirm ? (
          <button onClick={()=>setConfirm(true)} data-testid="sec-delete-start"
            className="px-5 py-2.5 rounded-full text-sm border" style={{borderColor:"rgba(255,59,48,0.5)", color:"#FF3B30"}}>
            Cancella il mio account
          </button>
        ) : (
          <form onSubmit={del} className="space-y-3">
            <input data-testid="sec-delete-pwd" type="password" required value={pwd} onChange={(e)=>setPwd(e.target.value)}
              placeholder="Conferma con la tua password"
              className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] focus:bg-white text-[#1A1A1A] transition-colors" />
            <div className="flex gap-2">
              <button type="button" onClick={()=>{setConfirm(false); setPwd("");}}
                className="px-4 py-2.5 rounded-full text-sm bg-[#F4EFE6] border border-[#F0EBE3]">Annulla</button>
              <button type="submit" disabled={loading} data-testid="sec-delete-confirm"
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-[#1A1A1A]"
                style={{background:"#FF3B30"}}>
                {loading ? "Cancellazione…" : "Cancella definitivamente"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
/* ========== RadiusSlider — km action range with brand-tinted track ========== */
function RadiusSlider({ icon: Icon, color, label, value, onChange, testid, min = 5, max = 100 }) {
  const pct = Math.round(((value - min) / (max - min)) * 100);
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${color}1F`, color, border: `1px solid ${color}33` }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[13px] text-[#1A1A1A]">{label}</div>
          <div className="text-[12px] font-mono tabular-nums" style={{ color }}>{value} km</div>
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={5}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          data-testid={`${testid}-input`}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer focus:outline-none"
          style={{
            background: `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, rgba(255,255,255,0.10) ${pct}%, rgba(255,255,255,0.10) 100%)`,
          }}
        />
        <div className="flex justify-between text-[10px] text-[#9A9A9A] mt-1 font-mono">
          <span>{min}km</span><span>{max}km</span>
        </div>
      </div>
    </div>
  );
}

/* ========== ProgressAvatar — centered avatar with completion ring ========== */
function ProgressAvatar({ photo, name, percent, onUpload }) {
  const size = 144;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  const accent = percent >= 100 ? "#34D399" : percent >= 60 ? "#FFB020" : "#FF3B30";

  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="progress-avatar">
      {/* SVG ring */}
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <defs>
          <linearGradient id="orio-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF3B30" />
            <stop offset="50%" stopColor="#FFB020" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
        </defs>
        {/* track */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="#F0EBE3"
          strokeWidth={stroke}
          fill="none"
        />
        {/* progress */}
        <motion.circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={percent >= 100 ? accent : "url(#orio-ring-grad)"}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          initial={{ strokeDasharray: `0 ${c}` }}
          animate={{ strokeDasharray: `${dash} ${c}` }}
          transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
          style={{ filter: `drop-shadow(0 0 6px ${accent}55)` }}
        />
      </svg>

      {/* Avatar */}
      <div
        className="absolute rounded-full overflow-hidden border border-[#F0EBE3] bg-gradient-to-br from-[#FF3B30]/20 via-[#FFB020]/20 to-[#34D399]/20 flex items-center justify-center"
        style={{ inset: stroke + 4, borderRadius: "9999px" }}
      >
        {photo ? (
          <img src={photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="font-display text-4xl text-[#9A9A9A]">
            {name?.[0]?.toUpperCase() || <UserIcon className="h-10 w-10 text-[#C7C0B3]" />}
          </span>
        )}
      </div>

      {/* Camera upload */}
      <label
        className="absolute h-9 w-9 rounded-full bg-white text-black flex items-center justify-center cursor-pointer hover:bg-white/90 transition-colors shadow-lg"
        style={{ bottom: 2, right: 2 }}
      >
        <Camera className="h-4 w-4" />
        <input data-testid="profile-photo" type="file" accept="image/*" onChange={onUpload} className="hidden" />
      </label>

      {/* Percentage badge */}
      <div
        className="absolute -top-1 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]"
        style={{
          background: "#FFFFFF",
          color: accent,
          border: `1.5px solid ${accent}`,
        }}
      >
        {percent}%
      </div>
    </div>
  );
}

/* ========== Profile completion helper ========== */
function computeCompletion(data, photoPreview) {
  const fields = [
    !!(data.name && data.name.trim().length > 1),
    !!(photoPreview || data.photo_url),
    !!(data.bio && data.bio.trim().length > 10),
    !!(data.city && data.city.trim().length > 1),
    !!data.birth_year,
    !!(data.phone && data.phone.trim().length > 5),
    !!data.dog_attitude,
    !!(data.interests && data.interests.length > 0),
  ];
  const filled = fields.filter(Boolean).length;
  return Math.round((filled / fields.length) * 100);
}


/* ========== PresenzaLiveCard — 15km live presence explainer with state pill ========== */
function PresenzaLiveCard() {
  const [visible, setVisible] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    api.get("/me/visibility")
      .then((r) => { if (!cancelled) setVisible(!!r.data?.visible); })
      .catch(() => { if (!cancelled) setVisible(false); });
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    setLoading(true);
    const next = !visible;
    try {
      // Grab current position — if not available, backend will still store toggle
      let payload = { visible: next };
      if (next && navigator.geolocation) {
        try {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000, enableHighAccuracy: false }));
          payload = { visible: next, lat: pos.coords.latitude, lng: pos.coords.longitude };
        } catch { /* no location — will need to activate again from map */ }
      }
      await api.put("/me/visibility", payload);
      setVisible(next);
      toast.success(next ? "Sei visibile su ORIO · raggio 15 km" : "Modalità invisibile attiva");
    } catch {
      toast.error("Errore aggiornamento visibilità");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="orio-card rounded-2xl p-5" data-testid="presenza-live-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(45,186,96,0.14)", color: "#2DBA60", border: "1px solid rgba(45,186,96,0.28)" }}>
          <Eye className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#2DBA60]">Presenza Live · 15 km</div>
          <div className="font-display text-lg mt-0.5">Vediti con chi è online in zona</div>
          <p className="text-[12px] text-[#9A9A9A] mt-1 leading-relaxed">
            Quando attivi la visibilità, appari sulla mappa con foto e nome a chi è nel raggio di
            <strong className="text-[#1A1A1A]"> 15 km</strong> — e vedi gli altri padroni ORIO. Raggio fisso, non modificabile
            (per equilibrio della community). Puoi accenderla o spegnerla dalla mappa o da qui.
          </p>
        </div>
      </div>

      {/* Current status pill + toggle */}
      <div className="mt-3 rounded-2xl p-3 flex items-center gap-3"
        style={{
          background: visible ? "linear-gradient(135deg, rgba(45,186,96,0.14) 0%, rgba(45,186,96,0.06) 100%)"
                              : "linear-gradient(135deg, #F5EFE6 0%, #EDE6D9 100%)",
          border: visible ? "1px solid rgba(45,186,96,0.35)" : "1px solid #E5DECF",
        }}
      >
        <div className="relative shrink-0">
          <span className={`block h-2.5 w-2.5 rounded-full ${visible ? "bg-[#2DBA60] animate-pulse" : "bg-[#B0A084]"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">
            {visible === null ? "Caricamento…" : visible ? "Attualmente visibile" : "Attualmente invisibile"}
          </div>
          <div className="text-[10.5px] text-[#7A7A7A] leading-tight mt-0.5">
            {visible ? "Aggiorno la tua posizione ogni 60s solo quando la mappa è aperta." : "Nessuno vede la tua posizione sulla mappa."}
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={loading || visible === null}
          data-testid="presenza-live-toggle"
          className="shrink-0 px-3.5 py-2 rounded-full text-[12px] font-semibold transition-all active:scale-[0.97]"
          style={{
            background: visible ? "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)" : "linear-gradient(135deg, #34D399 0%, #2DBA60 100%)",
            color: "#fff",
            boxShadow: visible ? "0 8px 18px -6px rgba(255,92,42,0.45)" : "0 8px 18px -6px rgba(45,186,96,0.45)",
          }}
        >
          {visible ? "Disattiva" : "Attiva"}
        </button>
      </div>

      {/* Info rows */}
      <div className="mt-4 space-y-2">
        <InfoRow icon={ShieldCheck} color="#FFB020" title="Le notifiche non dipendono dalla visibilità"
          text="Ricevi comunque tutti gli alert (pericoli, SOS, missioni) nel raggio configurato — anche in invisibile." />
        <InfoRow icon={Dot} color="#2DBA60" title="Sei online se hai la mappa aperta di recente"
          text="La tua presenza scade dopo 5 minuti di inattività — nessun tracking in background." />
        <InfoRow icon={EyeOff} color="#7B61FF" title="Disattivi e sparisci"
          text="Appena spegni la visibilità la tua ultima posizione viene rimossa dai server." />
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, color, title, text }) {
  return (
    <div className="flex items-start gap-2.5 px-2 py-1.5">
      <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-[12px] font-semibold text-[#1A1A1A] leading-tight">{title}</div>
        <div className="text-[10.5px] text-[#7A7A7A] leading-tight mt-0.5">{text}</div>
      </div>
    </div>
  );
}

/* ShelterCTASection — TR-style adaptive banner:
   - nessun rifugio → "Apri il tuo rifugio" con hero illustration
   - pending → schermata "In attesa di verifica"
   - verified → dashboard rapido con switch modalità Utente/Rifugio + CTA
*/
function ShelterCTASection() {
  const [state, setState] = React.useState({ loading: true, shelter: null });
  const [mode, setMode] = React.useState(
    () => (typeof window !== "undefined" && localStorage.getItem("orio-mode")) || "user"
  );
  React.useEffect(() => {
    api.get("/shelter/me")
      .then(({ data }) => setState({ loading: false, shelter: data.shelter || null }))
      .catch(() => setState({ loading: false, shelter: null }));
  }, []);
  const setModeAndSave = (m) => {
    setMode(m);
    try { localStorage.setItem("orio-mode", m); } catch { /* ignore */ }
  };
  if (state.loading) return null;
  const s = state.shelter;

  if (!s) {
    return (
      <div className="mt-6 rounded-3xl overflow-hidden border" style={{
        background: "linear-gradient(135deg, #FFF6E6 0%, #FFE8CB 100%)",
        borderColor: "#F0D9B0",
      }} data-testid="profile-shelter-open">
        <div className="p-5 flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center shrink-0"
            style={{ boxShadow: "0 8px 18px -6px rgba(255,122,26,0.35)" }}>
            <span className="text-2xl">🏠</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">Diventa rifugio</div>
            <div className="font-display text-lg leading-tight mt-0.5">Hai un rifugio o gestisci adozioni?</div>
            <p className="mt-1 text-[12.5px] text-[#4A4A4A] leading-relaxed">
              Registralo su ORIO: compila un modulo veloce, dopo la verifica compari sulla mappa,
              pubblichi cani in adozione e ricevi aiuti dalla community.
            </p>
            <Link to="/app/rifugio/registra"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold text-white"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                       boxShadow: "0 10px 22px -8px rgba(255,122,26,0.5)" }}>
              Apri modulo iscrizione →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (s.status === "pending") {
    return (
      <div className="mt-6 rounded-3xl p-5 border flex items-center gap-4"
        style={{ background: "#FFF6E6", borderColor: "#F0D9B0" }}
        data-testid="profile-shelter-pending">
        <div className="h-11 w-11 rounded-2xl bg-white flex items-center justify-center shrink-0">
          <span className="text-xl">⏳</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">Richiesta rifugio</div>
          <div className="font-semibold text-[14px] mt-0.5">In attesa di verifica</div>
          <div className="text-[11.5px] text-[#7A7A7A]">{s.name} — riceverai una notifica appena il team ORIO approva.</div>
        </div>
      </div>
    );
  }

  if (s.status === "rejected") {
    return (
      <div className="mt-6 rounded-3xl p-5 border flex items-center gap-4"
        style={{ background: "#FFEEDD", borderColor: "#F5B67A" }}>
        <div className="h-11 w-11 rounded-2xl bg-white flex items-center justify-center shrink-0">
          <span className="text-xl">❌</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">Rifugio non approvato</div>
          <div className="text-[12px] text-[#4A4A4A]">{s.rejection_reason || "Dati insufficienti"}</div>
          <Link to="/app/rifugio/registra"
            className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold text-[#FF7A1A]">
            Riprova →
          </Link>
        </div>
      </div>
    );
  }

  // Verified — dashboard rapida con mode switch
  return (
    <div className="mt-6 rounded-3xl overflow-hidden border" data-testid="profile-shelter-verified"
      style={{ background: "linear-gradient(135deg, #E8F4EA 0%, #C7E8CF 100%)", borderColor: "#A8D3B4" }}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white bg-[#2DBA60]">
              ✓ Rifugio verificato
            </div>
            <div className="font-display text-xl mt-2 leading-tight">{s.name}</div>
            <div className="text-[11.5px] text-[#3A3A3A] mt-0.5">{s.city}</div>
          </div>
          <Link to={`/app/rifugio/${s.id}`}
            className="shrink-0 h-9 px-3 rounded-full inline-flex items-center gap-1.5 text-[11.5px] font-semibold bg-white text-[#1A5A2E] border border-[#A8D3B4]">
            Vedi profilo pubblico →
          </Link>
        </div>

        {/* Mode switch */}
        <div className="mt-4 p-1 rounded-full bg-white/70 backdrop-blur inline-flex text-[12px] font-semibold"
             role="tablist" aria-label="Modalità profilo">
          {[
            { id: "user",    label: "Modalità utente", emoji: "👤" },
            { id: "shelter", label: "Modalità rifugio", emoji: "🏠" },
          ].map((m) => {
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setModeAndSave(m.id)}
                data-testid={`profile-mode-${m.id}`}
                className="px-3 py-1.5 rounded-full transition"
                style={{
                  background: active ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" : "transparent",
                  color: active ? "white" : "#1A5A2E",
                  boxShadow: active ? "0 6px 14px -6px rgba(255,122,26,0.45)" : "none",
                }}
              >
                {m.emoji} {m.label}
              </button>
            );
          })}
        </div>

        {/* Quick actions - shelter mode */}
        {mode === "shelter" ? (
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Link to="/app/cani" className="p-3 rounded-2xl bg-white border border-[#A8D3B4] text-center hover:shadow-md">
              <div className="text-xl">🐾</div>
              <div className="text-[11px] font-semibold text-[#1A5A2E] mt-1">Gestisci cani</div>
            </Link>
            <Link to="/app/rifugio" className="p-3 rounded-2xl bg-white border border-[#A8D3B4] text-center hover:shadow-md">
              <div className="text-xl">🎯</div>
              <div className="text-[11px] font-semibold text-[#1A5A2E] mt-1">Missioni</div>
            </Link>
            <Link to="/app/messaggi" className="p-3 rounded-2xl bg-white border border-[#A8D3B4] text-center hover:shadow-md">
              <div className="text-xl">💬</div>
              <div className="text-[11px] font-semibold text-[#1A5A2E] mt-1">Messaggi</div>
            </Link>
          </div>
        ) : (
          <div className="mt-3 text-[11.5px] text-[#3A3A3A]">
            Sei visibile su ORIO come utente. Passa in modalità rifugio per gestire cani, missioni e chat.
          </div>
        )}
      </div>
    </div>
  );
}
